// API Base URL Configuration
// 
// For local development: Uses Vite proxy to /api (configured in vite.config.ts)
// For production: Uses VITE_API_URL environment variable (set at build time) or
//                 tries to detect Worker URL from window location
//
// To configure in Cloudflare Pages:
// 1. Go to Cloudflare Dashboard > Pages > data-dictionary-frontend
// 2. Settings > Environment Variables
// 3. Add: VITE_API_URL = https://data-dictionary.YOUR_SUBDOMAIN.workers.dev/api
// 4. IMPORTANT: Redeploy the frontend after setting the variable (it's needed at build time)
//
// Alternative: Update the WORKER_SUBDOMAIN below with your actual subdomain
const WORKER_SUBDOMAIN = 'mburlworker'; // Replace with your actual Cloudflare subdomain

function getApiBaseUrl(): string {
  // 1. Try build-time environment variable (set in Cloudflare Pages)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // 2. In production, try to construct from known Worker URL pattern
  if (import.meta.env.PROD) {
    // If WORKER_SUBDOMAIN is set (not empty), use it
    if (WORKER_SUBDOMAIN && WORKER_SUBDOMAIN.trim() !== '') {
      return `https://data-dictionary.${WORKER_SUBDOMAIN}.workers.dev/api`;
    }
    
    // Try to detect from current domain (if Pages and Worker are on same account)
    // This is a fallback - may not always work
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    if (hostname.includes('pages.dev')) {
      // Extract account subdomain from Pages URL if possible
      // This is a best-effort attempt
      console.warn('API URL not configured. Please set VITE_API_URL in Cloudflare Pages or update WORKER_SUBDOMAIN in the code.');
    }
  }
  
  // 3. Development fallback (uses Vite proxy)
  return '/api';
}

const API_BASE_URL = getApiBaseUrl();

export interface DictionaryEntry {
  id?: number;
  origin_url?: string;
  who?: string;
  source_name: string;
  group_name: string;
  group_id: number;
  field_name: string;
  lookup_field_name?: string;
  app_label?: string;
  record_order?: number;
  column_order?: number;
  comments?: string;
  obscure?: string;
  conversion?: string;
  process_dataset?: number;
  is_column_in_source?: number;
  push_lp?: number;
  is_required?: number;
  is_ready?: number;
  is_key?: number;
  field_format?: string;
  sql_data_type?: string;
  sql_max_length?: number;
  version_id?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Group {
  id: number;
  group_name: string;
  group_id: number;
  description?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Version {
  id: number;
  version_number: number;
  description?: string;
  created_by?: string;
  created_at: string;
  is_active: number;
  is_approved: number;
}

export interface Comment {
  id: number;
  entry_id?: number;
  group_id?: number;
  comment_text: string;
  created_by?: string;
  created_at: string;
  parent_comment_id?: number;
}

export interface Approval {
  id: number;
  version_id: number;
  approver?: string;
  status: 'pending' | 'approved' | 'rejected';
  comments?: string;
  created_at: string;
}

async function fetchAPI<T>(endpoint: string, options?: RequestInit): Promise<T> {
  try {
    // Ensure endpoint starts with /
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${API_BASE_URL}${normalizedEndpoint}`;
    
    // Validate URL format
    try {
      new URL(url);
    } catch (urlError) {
      // If API_BASE_URL is relative and we're in production, this will fail
      if (import.meta.env.PROD && !API_BASE_URL.startsWith('http')) {
        throw new Error(
          'API URL not configured. Please set VITE_API_URL environment variable in Cloudflare Pages. ' +
          'Go to Pages > Settings > Environment Variables and add: VITE_API_URL = https://data-dictionary.mburlworker.workers.dev/api'
        );
      }
      throw urlError;
    }
    
    // Don't set Content-Type for FormData - browser will set it automatically with boundary
    const isFormData = options?.body instanceof FormData;
    const headers: HeadersInit = isFormData
      ? { ...options?.headers } // Let browser set Content-Type for FormData
      : {
          'Content-Type': 'application/json',
          ...options?.headers,
        };
    
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ 
        error: `Backend not responding (${response.status}). Check your API URL configuration.` 
      }));
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.data || data;
  } catch (error: any) {
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('pattern')) {
      if (import.meta.env.PROD) {
        throw new Error(
          'Cannot connect to backend API. Please configure VITE_API_URL in Cloudflare Pages environment variables. ' +
          'Your Worker URL should be: https://data-dictionary.mburlworker.workers.dev/api'
        );
      }
      throw new Error('Cannot connect to backend API. Please ensure the backend server is running on http://localhost:8787');
    }
    throw error;
  }
}

// Dictionary API
export const dictionaryAPI = {
  getAll: (filters?: {
    group_id?: number;
    source_name?: string;
    is_ready?: number;
    is_required?: number;
    process_dataset?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, value.toString());
      });
    }
    return fetchAPI<DictionaryEntry[]>(`/dictionary?${params.toString()}`);
  },

  getById: (id: number) => fetchAPI<DictionaryEntry>(`/dictionary/${id}`),

  create: (entry: Partial<DictionaryEntry>) =>
    fetchAPI<{ id: number }>('/dictionary', {
      method: 'POST',
      body: JSON.stringify(entry),
    }),

  update: (id: number, entry: Partial<DictionaryEntry>) =>
    fetchAPI<{ id: number }>(`/dictionary/${id}`, {
      method: 'PUT',
      body: JSON.stringify(entry),
    }),

  delete: (id: number) =>
    fetchAPI<void>(`/dictionary/${id}`, {
      method: 'DELETE',
    }),

  getGroups: () => fetchAPI<Group[]>('/dictionary/groups'),

  getGroup: (groupId: number) =>
    fetchAPI<{ group: Group; entries: DictionaryEntry[] }>(`/dictionary/groups/${groupId}`),
};

// Import/Export API
export const importExportAPI = {
  importCSV: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetchAPI<any>('/import/csv', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type with boundary
    });
  },

  importSQL: (sql: string, groupId: number, originUrl?: string, who?: string) =>
    fetchAPI<any>('/import/sql', {
      method: 'POST',
      body: JSON.stringify({ sql, groupId, originUrl, who }),
    }),

  exportCSV: (groupId?: number) => {
    const url = groupId ? `/export/csv/${groupId}` : '/export/csv';
    return fetch(url).then((res) => res.blob());
  },
};

// Versions API
export const versionsAPI = {
  getAll: () => fetchAPI<Version[]>('/versions'),

  getById: (id: number) => fetchAPI<Version>(`/versions/${id}`),

  create: (description?: string, createdBy?: string) =>
    fetchAPI<{ version_id: number; version_number: number; snapshot_count: number }>('/versions', {
      method: 'POST',
      body: JSON.stringify({ description, created_by: createdBy }),
    }),

  activate: (id: number) =>
    fetchAPI<void>(`/versions/${id}/activate`, {
      method: 'POST',
    }),

  getDiff: (id: number, compareWith?: number) => {
    const url = compareWith
      ? `/versions/${id}/diff?compare_with=${compareWith}`
      : `/versions/${id}/diff`;
    return fetchAPI<any>(url);
  },
};

// Comments API
export const commentsAPI = {
  getByEntry: (entryId: number) => fetchAPI<Comment[]>(`/comments/entry/${entryId}`),

  getByGroup: (groupId: number) => fetchAPI<Comment[]>(`/comments/group/${groupId}`),

  create: (comment: {
    entry_id?: number;
    group_id?: number;
    comment_text: string;
    created_by?: string;
    parent_comment_id?: number;
  }) =>
    fetchAPI<{ id: number }>('/comments', {
      method: 'POST',
      body: JSON.stringify(comment),
    }),

  update: (id: number, comment_text: string) =>
    fetchAPI<void>(`/comments/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ comment_text }),
    }),

  delete: (id: number) =>
    fetchAPI<void>(`/comments/${id}`, {
      method: 'DELETE',
    }),
};

// Approvals API
export const approvalsAPI = {
  getAll: (status?: string) => {
    const url = status ? `/approvals?status=${status}` : '/approvals';
    return fetchAPI<Approval[]>(url);
  },

  getById: (id: number) => fetchAPI<Approval>(`/approvals/${id}`),

  create: (versionId: number, approver?: string, comments?: string) =>
    fetchAPI<{ id: number }>('/approvals', {
      method: 'POST',
      body: JSON.stringify({ version_id: versionId, approver, comments }),
    }),

  update: (id: number, status: 'pending' | 'approved' | 'rejected', comments?: string) =>
    fetchAPI<void>(`/approvals/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status, comments }),
    }),

  delete: (id: number) =>
    fetchAPI<void>(`/approvals/${id}`, {
      method: 'DELETE',
    }),
};

